import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import { useToast } from "@/context/ToastContext";
import { Icon } from "@/components/Icon";
import { developmentApi } from "@/lib/api";
import { Modal } from "@/components/Modal";
import { Markdown } from "@/components/Markdown";
import { Tooltip } from "@/components/Tooltip";
import { Tabs } from "@/components/Tabs";
import { AttachmentThumbnail } from "@/components/AttachmentThumbnail";
import { MicrophoneButton } from "@/components/MicrophoneButton";
import { Textarea } from "@/components/Textarea";
import { SearchableMultiSelect } from "@/components/SearchableMultiSelect";
import { Switch } from "@/components/Switch";
import { FileViewer } from "@/components/FileViewer";
import { Alert } from "@/components/Alert";
import {
    ImageLightboxModal,
    isImageFile,
    LightboxItem,
} from "@/components/ImageLightboxModal";
import {
    hasRichFormatting,
    htmlToPlainText,
    cleanHtmlContent,
    price,
    quantity,
} from "@/utils/format";

interface AskAiModalProps {
    isOpen: boolean;
    onClose: () => void;
    development: {
        id: number;
        title?: string;
        code?: string;
        attachments?: Array<{
            id: number;
            name: string;
            file_path?: string;
            category?: string | null;
            mime_type?: string;
            file_size?: number;
            created_at?: string;
        }>;
    };
    onMessageAdded?: (msg: unknown) => void | Promise<void>;
    onAttachmentsUpdated?: () => void | Promise<void>;
    initialPrompt?: string;
    initialResponse?: string;
    replyToMessageId?: number | null;
    showQuickActions?: boolean;
    attachments?: Array<{
        id: number;
        name: string;
        mime_type?: string;
        file_size?: number;
    }>;
    conversationText?: string;
    isDesignMode?: boolean;
    initialRequirementDocId?: number;
}

interface AiResultMetadata {
    format: string;
    provider: string;
    model: string;
}

const extractHtmlContent = (text: string | null): string | null => {
    if (!text) {
        return null;
    }

    if (
        text.includes("<!DOCTYPE html>") ||
        (text.includes("<html") && text.includes("</html>"))
    ) {
        return cleanHtmlContent(text);
    }

    const codeBlockRegex = /```(?:html|xml|jsx|tsx)?\s*([\s\S]*?)```/gi;
    let match: RegExpExecArray | null;
    while ((match = codeBlockRegex.exec(text)) !== null) {
        const content = cleanHtmlContent(match[1]);
        if (content && content.includes("<") && content.includes(">")) {
            return content;
        }
    }

    if (text.length < 50000) {
        const htmlTagMatch = text.match(
            /<(div|main|section|header|table)[\s\S]*?<\/(div|main|section|header|table)>/i
        );
        if (htmlTagMatch) {
            return cleanHtmlContent(htmlTagMatch[0]);
        }
    }

    if (text.includes("```html") || text.includes("```")) {
        const cleaned = cleanHtmlContent(text);
        if (cleaned.includes("<") && cleaned.includes(">")) {
            return cleaned;
        }
    }

    return null;
};

const buildStandaloneHtml = (
    htmlContent: string,
    pageTitle: string
): string => {
    const cleaned = cleanHtmlContent(htmlContent);
    const sharedTailwindScript = `<script src="https://cdn.tailwindcss.com"></script><link href="https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700&display=swap" rel="stylesheet"><script>tailwind.config={theme:{extend:{colors:{primary:{DEFAULT:'#a33835',50:'#fbf5f5',100:'#f7ebea',200:'#f0dad8',300:'#e2bebb',400:'#cf9792',500:'#ba6f6a',600:'#a33835',700:'#8c2f2c',800:'#742725',900:'#5e201e',950:'#3b1211'}},fontFamily:{sans:['Outfit','sans-serif']}}}}</script><style>
      body { font-family: 'Outfit', sans-serif; background-color: #f8fafc; color: #1e293b; margin: 0; padding: 0; }
      .bg-box { background-color: #ffffff; }
      .border-box { border-color: #e2e8f0; }
      .btn { display: inline-flex; align-items: center; justify-content: center; gap: 0.5rem; font-weight: 600; font-size: 0.8125rem; border-radius: 0.5rem; transition: all 0.15s ease; cursor: pointer; padding: 0.5rem 1rem; }
      .btn.primary, .btn-primary { background-color: #a33835 !important; color: #ffffff !important; border: 1px solid transparent; }
      .btn.primary:hover, .btn-primary:hover { background-color: #8c2f2c !important; }
      .btn.secondary, .btn-secondary { background-color: #f1f5f9; color: #334155; border: 1px solid #cbd5e1; }
      .btn.secondary:hover, .btn-secondary:hover { background-color: #e2e8f0; }
      .btn.danger, .btn-danger { background-color: #ef4444; color: #ffffff; }
      .badge { display: inline-flex; align-items: center; gap: 0.25rem; padding: 0.2rem 0.6rem; border-radius: 9999px; font-size: 0.75rem; font-weight: 600; }
      .badge-success { background-color: #ecfdf5; color: #047857; border: 1px solid #a7f3d0; }
      .badge-warning { background-color: #fffbeb; color: #b45309; border: 1px solid #fde68a; }
      .badge-danger { background-color: #fef2f2; color: #b91c1c; border: 1px solid #fecaca; }
      .badge-info { background-color: #eff6ff; color: #1d4ed8; border: 1px solid #bfdbfe; }
      input[type="text"], input[type="search"], input[type="email"], select, textarea { width: 100%; padding: 0.5rem 0.75rem; border: 1px solid #cbd5e1; border-radius: 0.5rem; font-size: 0.8125rem; outline: none; background-color: #ffffff; }
      input:focus, select:focus, textarea:focus { border-color: #a33835; box-shadow: 0 0 0 2px rgba(163, 56, 53, 0.2); }
      .screen-nav-btn.active { background-color: #a33835 !important; color: #ffffff !important; }
    </style>`;

    if (
        cleaned.includes("<!DOCTYPE html>") ||
        (cleaned.includes("<html") && cleaned.includes("</html>"))
    ) {
        if (
            !cleaned.includes("tailwindcss.com") &&
            !cleaned.includes("vite-compiled")
        ) {
            return cleaned.replace(/<head[^>]*>/i, `$&${sharedTailwindScript}`);
        }
        return cleaned;
    }

    return `<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${pageTitle}</title>
  ${sharedTailwindScript}
</head>
<body class="bg-slate-50 text-slate-900 min-h-screen">
  <div id="prototype-container" class="max-w-7xl mx-auto p-6 space-y-6">
    ${cleaned}
  </div>

  <script>
    document.addEventListener('DOMContentLoaded', () => {
      // 1. Detect screens and build top switcher if missing
      const screens = document.querySelectorAll('.screen-view, [data-screen], section[id*="screen"], div[id*="screen"]');
      const container = document.getElementById('prototype-container');

      if(screens.length > 1 && !document.querySelector('.prototype-nav') && container){
        const navHeader = document.createElement('header');
        navHeader.className = 'prototype-nav sticky top-0 z-50 bg-white/95 backdrop-blur-xs border border-slate-200 rounded-2xl p-3 mb-6 flex items-center justify-between shadow-xs flex-wrap gap-3';

        const titleDiv = document.createElement('div');
        titleDiv.className = 'flex items-center gap-2';
        titleDiv.innerHTML = '<span class="w-3 h-3 rounded-full bg-[#a33835] inline-block animate-pulse"></span><span class="text-xs font-bold text-slate-800 uppercase tracking-wider">Pantallas Diseñadas</span>';

        const tabsDiv = document.createElement('nav');
        tabsDiv.className = 'flex items-center gap-1.5 flex-wrap';

        screens.forEach((screen, idx) => {
          const screenTitle = screen.getAttribute('data-screen-title')
            || screen.getAttribute('data-title')
            || screen.querySelector('h1, h2, h3')?.textContent?.trim()
            || ('Pantalla ' + (idx + 1));

          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'screen-nav-btn px-3 py-1.5 text-xs font-semibold rounded-xl border border-slate-200 transition-all cursor-pointer ' + (idx === 0 ? 'active bg-[#a33835] text-white shadow-xs' : 'bg-slate-50 text-slate-700 hover:bg-slate-100');
          btn.textContent = screenTitle;

          btn.addEventListener('click', () => {
            screens.forEach((s, sIdx) => {
              if(sIdx === idx){
                s.classList.remove('hidden');
                s.style.display = 'block';
              }
              else {
                s.classList.add('hidden');
                s.style.display = 'none';
              }
            });
            document.querySelectorAll('.screen-nav-btn').forEach((b, bIdx) => {
              if(bIdx === idx){
                b.className = 'screen-nav-btn active px-3 py-1.5 text-xs font-semibold rounded-xl border border-slate-200 transition-all cursor-pointer bg-[#a33835] text-white shadow-xs';
              }
              else {
                b.className = 'screen-nav-btn px-3 py-1.5 text-xs font-semibold rounded-xl border border-slate-200 transition-all cursor-pointer bg-slate-50 text-slate-700 hover:bg-slate-100';
              }
            });
          });

          tabsDiv.appendChild(btn);

          if(idx === 0){
            screen.classList.remove('hidden');
            screen.style.display = 'block';
          }
          else {
            screen.classList.add('hidden');
            screen.style.display = 'none';
          }
        });

        navHeader.appendChild(titleDiv);
        navHeader.appendChild(tabsDiv);
        container.insertBefore(navHeader, container.firstChild);
      }

      // 2. Click handlers for links between screens
      document.querySelectorAll('[data-screen-target], [data-screen-to], a[href^="#screen-"]').forEach(link => {
        link.addEventListener('click', (e) => {
          const target = link.getAttribute('data-screen-target') || link.getAttribute('data-screen-to') || link.getAttribute('href');
          if(target){
            e.preventDefault();
            const clean = target.replace('#', '');
            screens.forEach(s => {
              if(s.id === clean || s.getAttribute('data-screen') === clean){
                s.classList.remove('hidden');
                s.style.display = 'block';
              }
              else {
                s.classList.add('hidden');
                s.style.display = 'none';
              }
            });
          }
        });
      });

      // 3. Modal open / close handlers
      document.querySelectorAll('[data-modal-target], [data-open-modal]').forEach(btn => {
        btn.addEventListener('click', () => {
          const id = btn.getAttribute('data-modal-target') || btn.getAttribute('data-open-modal');
          const m = document.getElementById(id);
          if(m){
            m.classList.remove('hidden');
            m.style.display = 'flex';
          }
        });
      });

      document.querySelectorAll('[data-modal-close], [data-close-modal]').forEach(btn => {
        btn.addEventListener('click', () => {
          const m = btn.closest('[id*="modal"]') || btn.closest('.fixed');
          if(m){
            m.classList.add('hidden');
            m.style.display = 'none';
          }
        });
      });

      window.addEventListener('keydown', (e) => {
        if(e.key === 'Escape'){
          document.querySelectorAll('[id*="modal"]').forEach(m => {
            m.classList.add('hidden');
            m.style.display = 'none';
          });
        }
      });
    });
  </script>
</body>
</html>`;
};

export const AskAiModal: React.FC<AskAiModalProps> = ({
    isOpen,
    onClose,
    development,
    onMessageAdded,
    onAttachmentsUpdated,
    initialPrompt,
    initialResponse,
    replyToMessageId,
    showQuickActions = true,
    attachments = [],
    conversationText,
    isDesignMode = false,
    initialRequirementDocId,
}) => {
    const { t } = useTranslation("global");
    const { showSuccess, showError } = useToast();
    const [prompt, setPrompt] = useState<string>(t("ai_modal.prompt.generic"));
    const [basePromptForRecording, setBasePromptForRecording] = useState<
        string | null
    >(null);
    const [contextType, setContextType] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [aiResult, setAiResult] = useState<string | null>(null);
    const [aiMetadata, setAiMetadata] = useState<AiResultMetadata | null>(null);
    const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
    const [viewTab, setViewTab] = useState<"preview" | "code">("preview");
    const previewIframeRef = useRef<HTMLIFrameElement | null>(null);
    const [conversation, setConversation] = useState<
        Array<{ prompt: string; content: string }>
    >([]);
    const [lastPrompt, setLastPrompt] = useState("");
    const [copied, setCopied] = useState<boolean>(false);
    const [chatCopied, setChatCopied] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [contextLanguage, setContextLanguage] = useState<"it" | "client">(
        "it"
    );
    const [includeContext, setIncludeContext] = useState<boolean>(true);
    const [aiProvider, setAiProvider] = useState<"local" | "openai">("local");
    const [defaultAiProvider, setDefaultAiProvider] = useState<
        "local" | "openai"
    >("local");
    const hasInitializedPrompt = useRef(false);
    const [aiAttachments, setAiAttachments] = useState<
        Array<{
            id: number;
            name: string;
            file_size?: number;
            mime_type?: string;
        }>
    >([]);
    const [uploadingFiles, setUploadingFiles] = useState<boolean>(false);
    const inlineFileInputRef = useRef<HTMLInputElement>(null);
    const fullscreenFileInputRef = useRef<HTMLInputElement>(null);
    const dragCounter = useRef(0);
    const [isDraggingFile, setIsDraggingFile] = useState(false);

    // Lightbox State
    const [showLightbox, setShowLightbox] = useState(false);
    const [lightboxItems, setLightboxItems] = useState<LightboxItem[]>([]);
    const [lightboxIndex, setLightboxIndex] = useState(0);

    const [fileViewerTarget, setFileViewerTarget] = useState<{
        filename: string;
        mimeType?: string;
        fileSize?: number;
        loadBlob: () => Promise<Blob>;
    } | null>(null);

    const requirementDocs = useMemo(() => {
        const list = (development.attachments ?? []).filter((att) => {
            const isDoc = att.category === "requirements_document";
            return (
                isDoc &&
                (att.name.toLowerCase().endsWith(".md") ||
                    att.name.toLowerCase().endsWith(".markdown"))
            );
        });

        return list.sort((a, b) => b.id - a.id);
    }, [development.attachments]);

    const [generatedRequirementDocId, setGeneratedRequirementDocId] = useState<
        number | null
    >(null);
    const [selectedRequirementDocId, setSelectedRequirementDocId] = useState<
        number | null
    >(null);
    const [isDocSelectFullscreen, setIsDocSelectFullscreen] =
        useState<boolean>(false);

    const extractedHtml = useMemo(
        () => extractHtmlContent(aiResult),
        [aiResult]
    );
    const standaloneHtml = useMemo(() => {
        if (!extractedHtml) {
            return null;
        }

        const devCodeOrId =
            development.code || `DEV${String(development.id).padStart(3, "0")}`;
        return buildStandaloneHtml(
            extractedHtml,
            `Diseño - ${development.title || devCodeOrId}`
        );
    }, [extractedHtml, development.title, development.code, development.id]);

    const handleOpenPreviewNewTab = (): void => {
        const attachmentUrl = (aiMetadata as any)?.attachment_url;
        if (attachmentUrl) {
            window.open(attachmentUrl, "_blank");
            return;
        }

        if (standaloneHtml) {
            const newWindow = window.open("", "_blank");
            if (newWindow) {
                newWindow.document.open();
                newWindow.document.write(standaloneHtml);
                newWindow.document.close();
                return;
            }

            const blob = new Blob([standaloneHtml], {
                type: "text/html;charset=utf-8",
            });
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.target = "_blank";
            link.rel = "noopener noreferrer";
            link.click();
            setTimeout(() => URL.revokeObjectURL(url), 10000);
            return;
        }
    };

    useEffect(() => {
        if (!isOpen) {
            hasInitializedPrompt.current = false;
            return;
        }

        if (hasInitializedPrompt.current) {
            return;
        }

        hasInitializedPrompt.current = true;

        if (isDesignMode) {
            const source = development.attachments?.find(
                (doc) => doc.id === initialRequirementDocId
            );
            const sourceStem = source?.name.replace(
                /\.(pdf|md|markdown)$/i,
                ""
            );
            const selected = requirementDocs.find(
                (doc) =>
                    doc.id === initialRequirementDocId ||
                    (sourceStem &&
                        doc.name.replace(/\.(md|markdown)$/i, "") ===
                            sourceStem)
            );
            setSelectedRequirementDocId(
                selected?.id ?? requirementDocs[0]?.id ?? null
            );
        }

        const rawPrompt =
            initialPrompt && initialPrompt.trim() !== "" ? initialPrompt : "";
        const normalizedInitialPrompt = rawPrompt
            ? !hasRichFormatting(rawPrompt)
                ? htmlToPlainText(rawPrompt).trim()
                : rawPrompt
            : "";
        const defaultPrompt = initialResponse
            ? ""
            : normalizedInitialPrompt !== ""
              ? normalizedInitialPrompt
              : isDesignMode
                ? t("ai_modal.prompt.design_generic", {
                      title: development?.title,
                  })
                : contextLanguage === "client"
                  ? t("ai_modal.prompt.generic_client")
                  : t("ai_modal.prompt.generic");
        setPrompt(defaultPrompt);
        setConversation(
            initialResponse
                ? [
                      {
                          prompt: initialPrompt ?? "",
                          content: initialResponse,
                      },
                  ]
                : []
        );
        setAiResult(null);
        setAiMetadata(null);
        setContextType(isDesignMode ? "design" : null);
        setIncludeContext(true);
        setAiProvider(defaultAiProvider);
    }, [
        isOpen,
        initialPrompt,
        initialResponse,
        isDesignMode,
        initialRequirementDocId,
        requirementDocs,
        replyToMessageId,
        defaultAiProvider,
    ]);

    useEffect(() => {
        let isMounted = true;
        const fetchAiConfig = async (): Promise<void> => {
            try {
                const res = await developmentApi.getAiConfig(development?.id);
                if (isMounted && res?.result && res?.data?.default_provider) {
                    const prov =
                        res.data.default_provider === "openai"
                            ? "openai"
                            : "local";
                    setDefaultAiProvider(prov);
                    setAiProvider(prov);
                }
            } catch {
                // Ignore error, keep current
            }
        };

        if (isOpen) {
            void fetchAiConfig();
        }

        return () => {
            isMounted = false;
        };
    }, [isOpen, development?.id]);

    const isClientMode = contextLanguage === "client";

    const handleSelectProvider = (provider: "local" | "openai"): void => {
        if (provider !== aiProvider) {
            if (aiMetadata) {
                void deleteOrphanedAttachment(aiMetadata);
            }

            if (lastPrompt && !prompt.trim()) {
                setPrompt(lastPrompt);
            }

            setAiResult(null);
            setAiMetadata(null);
            setError(null);
            setViewTab("preview");
        }

        setAiProvider(provider);
    };

    const handleSelectLanguage = (lang: "it" | "client"): void => {
        setContextLanguage(lang);
        const genericIt = t("ai_modal.prompt.generic");
        const genericClient = t("ai_modal.prompt.generic_client");
        if (
            !prompt.trim() ||
            prompt === genericIt ||
            prompt === genericClient
        ) {
            setPrompt(lang === "client" ? genericClient : genericIt);
        }
    };

    const PRESETS = [
        {
            id: "analysis",
            title: t("ai_modal.preset_analysis"),
            icon: "search",
            prompt: isClientMode
                ? t("ai_modal.prompt.analysis_client", {
                      title: development?.title,
                  })
                : t("ai_modal.prompt.analysis", { title: development?.title }),
        },
        {
            id: "client_questions",
            title: t("ai_modal.preset_questions_client"),
            icon: "info",
            prompt: t("ai_modal.prompt.questions_client", {
                title: development?.title,
            }),
        },
        {
            id: "client_reply",
            title: t("ai_modal.preset_client_reply"),
            icon: "message",
            prompt: t("ai_modal.prompt.client_reply", {
                title: development?.title,
            }),
        },
        {
            id: "jira",
            title: t("ai_modal.preset_jira"),
            icon: "list",
            prompt: t("ai_modal.prompt.jira", { title: development?.title }),
        },
        {
            id: "risks",
            title: t("ai_modal.preset_risks"),
            icon: "shieldAlert",
            prompt: t("ai_modal.prompt.risks", { title: development?.title }),
        },
    ];

    const DESIGN_PRESETS = [
        {
            id: "design_full",
            title: t("ai_modal.preset_design_full"),
            icon: "palette",
            prompt: t("ai_modal.prompt.design_full", {
                title: development?.title,
            }),
        },
        {
            id: "design_html",
            title: t("ai_modal.preset_design_html"),
            icon: "code",
            prompt: t("ai_modal.prompt.design_html", {
                title: development?.title,
            }),
        },
        {
            id: "design_components",
            title: t("ai_modal.preset_design_components"),
            icon: "table",
            prompt: t("ai_modal.prompt.design_components", {
                title: development?.title,
            }),
        },
        {
            id: "design_flows",
            title: t("ai_modal.preset_design_flows"),
            icon: "listOrdered",
            prompt: t("ai_modal.prompt.design_flows", {
                title: development?.title,
            }),
        },
    ];

    const activePresets = isDesignMode ? DESIGN_PRESETS : PRESETS;

    const handleApplyPreset = (preset: {
        id: string;
        prompt: string;
    }): void => {
        setContextType(preset.id);
        setPrompt(preset.prompt);
        setAiResult(null);
        setAiMetadata(null);
    };

    const handleAiFilesSelection = async (files: File[]) => {
        if (files.length === 0) return;

        setUploadingFiles(true);

        try {
            for (const file of files) {
                const res = await developmentApi.uploadAttachment(
                    development.id,
                    file,
                    null,
                    true,
                    "ai"
                );

                if (res.result && res.data) {
                    setAiAttachments((prev) => [...prev, res.data]);
                } else {
                    showError(
                        res.message || t("developments.detail.unexpected_error")
                    );
                }
            }

            await onAttachmentsUpdated?.();
        } catch (err) {
            showError(
                err instanceof Error
                    ? err.message
                    : t("developments.detail.unexpected_error")
            );
        } finally {
            setUploadingFiles(false);
        }
    };

    const handleAiFileUpload = async (
        e: React.ChangeEvent<HTMLInputElement>
    ): Promise<void> => {
        const files = Array.from(e.target.files ?? []);
        e.target.value = "";
        await handleAiFilesSelection(files);
    };

    const handleDragEnter = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        dragCounter.current++;
        if (e.dataTransfer?.items && e.dataTransfer.items.length > 0) {
            setIsDraggingFile(true);
        }
    };

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        dragCounter.current--;
        if (dragCounter.current === 0) {
            setIsDraggingFile(false);
        }
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = async (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDraggingFile(false);
        dragCounter.current = 0;

        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            const files = Array.from(e.dataTransfer.files);
            await handleAiFilesSelection(files);
        }
    };

    const handleRemoveAiAttachment = async (
        attachmentId: number
    ): Promise<void> => {
        try {
            await developmentApi.deleteAttachment(development.id, attachmentId);
            setAiAttachments((prev) =>
                prev.filter((att) => att.id !== attachmentId)
            );
            await onAttachmentsUpdated?.();
        } catch (err) {
            console.error("Error removing AI attachment:", err);
        }
    };

    const handleAiAttachmentClick = (att: {
        id: number;
        name: string;
        mime_type?: string;
        file_size?: number;
    }): void => {
        if (isImageFile(att.name, att.mime_type)) {
            setLightboxItems([
                {
                    id: att.id,
                    title: att.name,
                    loadBlob: () =>
                        developmentApi.getAttachmentBlob(
                            development.id,
                            att.id
                        ),
                    canDelete: false,
                },
            ]);
            setLightboxIndex(0);
            setShowLightbox(true);
        } else {
            setFileViewerTarget({
                filename: att.name,
                mimeType: att.mime_type,
                fileSize: att.file_size,
                loadBlob: () =>
                    developmentApi.getAttachmentBlob(development.id, att.id),
            });
        }
    };

    const renderAttachedChips = (): React.ReactNode => {
        if (aiAttachments.length === 0) {
            return null;
        }

        return (
            <div className="flex flex-wrap gap-2 pt-1 pb-2">
                {aiAttachments.map((att) => (
                    <span
                        key={att.id}
                        className="inline-flex items-center h-9 rounded-xl bg-primary text-white text-xs font-medium shadow-xs shrink-0 select-none overflow-hidden"
                    >
                        <button
                            type="button"
                            onClick={() => handleAiAttachmentClick(att)}
                            className="inline-flex items-center gap-1.5 h-full px-3 cursor-pointer hover:bg-white/10 transition-colors min-w-0 max-w-[220px] text-white bg-transparent border-0"
                            title={att.name}
                        >
                            <Icon
                                value={
                                    isImageFile(att.name, att.mime_type)
                                        ? "eye"
                                        : "paperclip"
                                }
                                size={13}
                                className="shrink-0"
                            />
                            <span className="truncate">{att.name}</span>
                        </button>
                        <button
                            type="button"
                            onClick={() => handleRemoveAiAttachment(att.id)}
                            disabled={loading}
                            className="h-full px-2 hover:bg-white/20 cursor-pointer transition-colors text-white/90 hover:text-white shrink-0 bg-transparent border-0"
                            title={t("developments.detail.remove_file")}
                        >
                            <Icon value="x" size={14} />
                        </button>
                    </span>
                ))}
            </div>
        );
    };

    const fullscreenExtraContent = (
        <div className="w-full shrink-0 pt-2 space-y-2">
            <input
                ref={fullscreenFileInputRef}
                type="file"
                multiple
                onChange={handleAiFileUpload}
                className="hidden"
            />
            <div className="flex items-center gap-2 flex-wrap mb-2">
                <button
                    type="button"
                    onClick={() => fullscreenFileInputRef.current?.click()}
                    disabled={uploadingFiles || loading}
                    className="h-9 px-3 text-xs font-semibold rounded-xl border border-slate-200 dark:border-white/20 hover:bg-slate-100 dark:hover:bg-white/10 flex items-center gap-1.5 cursor-pointer shrink-0 bg-white dark:bg-dark-box shadow-2xs disabled:opacity-50"
                >
                    {uploadingFiles ? (
                        <Icon
                            value="loader"
                            size={14}
                            className="animate-spin text-primary"
                        />
                    ) : (
                        <Icon value="paperclip" size={14} />
                    )}
                    <span>
                        {uploadingFiles
                            ? t("developments.detail.uploading_file")
                            : t("developments.detail.attach_files")}
                    </span>
                </button>

                {aiAttachments.map((att) => (
                    <span
                        key={att.id}
                        className="inline-flex items-center h-9 rounded-xl bg-primary text-white text-xs font-medium shadow-xs shrink-0 select-none overflow-hidden"
                    >
                        <button
                            type="button"
                            onClick={() => handleAiAttachmentClick(att)}
                            className="inline-flex items-center gap-1.5 h-full px-3 cursor-pointer hover:bg-white/10 transition-colors min-w-0 max-w-[220px] text-white bg-transparent border-0"
                            title={att.name}
                        >
                            <Icon
                                value={
                                    isImageFile(att.name, att.mime_type)
                                        ? "eye"
                                        : "paperclip"
                                }
                                size={13}
                                className="shrink-0"
                            />
                            <span className="truncate">{att.name}</span>
                        </button>
                        <button
                            type="button"
                            onClick={() => handleRemoveAiAttachment(att.id)}
                            disabled={loading}
                            className="h-full px-2 hover:bg-white/20 cursor-pointer transition-colors text-white/90 hover:text-white shrink-0 bg-transparent border-0"
                            title={t("developments.detail.remove_file")}
                        >
                            <Icon value="x" size={14} />
                        </button>
                    </span>
                ))}
            </div>
        </div>
    );

    const handleGenerate = async (): Promise<void> => {
        if (!prompt.replace(/<[^>]*>/g, "").trim()) {
            return;
        }

        let currentConversation = [...conversation];
        if (aiResult) {
            currentConversation = [
                ...currentConversation,
                { prompt: lastPrompt || prompt.trim(), content: aiResult },
            ];
            setConversation(currentConversation);
        }

        setLoading(true);
        setError(null);
        setAiResult(null);
        setAiMetadata(null);

        try {
            const toneInstruction = isDesignMode
                ? t("ai_modal.system_prompts.design_tone")
                : contextLanguage === "client"
                  ? t("ai_modal.system_prompts.client_tone")
                  : t("ai_modal.system_prompts.it_tone");

            const noTitleInstruction = t("ai_modal.system_prompts.no_title");

            const attachmentsInfo =
                aiAttachments.length > 0
                    ? t("ai_modal.system_prompts.attachments_info", {
                          files: aiAttachments.map((a) => a.name).join(", "),
                      })
                    : "";

            const conversationContext = currentConversation
                .map(
                    (turn) =>
                        `${t("ai_modal.system_prompts.history_query", { query: turn.prompt })}\n${t("ai_modal.system_prompts.history_response", { response: turn.content })}`
                )
                .join("\n\n");

            const promptToSend = `${conversationContext ? conversationContext + "\n\n" : ""}${t(
                "ai_modal.system_prompts.current_query",
                {
                    query: prompt.trim(),
                    attachments: attachmentsInfo,
                    tone: toneInstruction,
                    noTitle: noTitleInstruction,
                }
            )}`;

            const res = await developmentApi.askAi(development.id, {
                prompt: promptToSend,
                context_type: isDesignMode ? "design" : contextType,
                include_context: includeContext,
                provider: aiProvider,
                requirement_attachment_id: isDesignMode
                    ? selectedRequirementDocId
                    : undefined,
            });
            if (res.result && res.data) {
                setLastPrompt(prompt.trim());
                setGeneratedRequirementDocId(selectedRequirementDocId);
                setAiResult(res.data.content);
                setAiMetadata({
                    format: res.data.format,
                    provider: res.data.provider,
                    model: res.data.model,
                    usage: res.data.usage,
                    cost: res.data.cost,
                    attachment_id: res.data.attachment_id,
                    attachment_url: res.data.attachment_url,
                });
                setViewTab("preview");
                setPrompt("");
            } else {
                setError(res.message);
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : "Error");
        } finally {
            setLoading(false);
        }
    };

    const deleteOrphanedAttachment = async (metadata: any) => {
        if (metadata?.attachment_id) {
            try {
                await developmentApi.deleteAttachment(
                    development.id,
                    metadata.attachment_id
                );
            } catch (e) {
                console.error("Failed to delete orphaned AI attachment", e);
            }
        }
    };

    const handleClose = (): void => {
        if (aiMetadata) {
            void deleteOrphanedAttachment(aiMetadata);
        }
        onClose();
        setTimeout(() => {
            setConversation([]);
            setAiResult(null);
            setAiMetadata(null);
            setError(null);
            setCopied(false);
            setContextType(null);
            setIsDocSelectFullscreen(false);
            setAiAttachments([]);
        }, 350);
    };

    const handleAccept = async (): Promise<void> => {
        if (!aiResult || !aiMetadata) {
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const combinedContent =
                conversation.length > 0
                    ? `${conversation.map((turn, i) => `### Consulta ${i + 1}\n${turn.prompt}\n\n### Respuesta\n${turn.content}`).join("\n\n---\n\n")}\n\n---\n\n### Consulta ${conversation.length + 1}\n${lastPrompt}\n\n### Respuesta\n${aiResult}`
                    : aiResult;

            // In design mode, build a clean summary instead of the full AI content
            let messageContent = combinedContent;
            if (isDesignMode) {
                const sourceDocId =
                    generatedRequirementDocId || selectedRequirementDocId;
                const sourceDoc = sourceDocId
                    ? requirementDocs.find((d) => d.id === sourceDocId)
                    : null;
                const lines: string[] = [];
                lines.push(`🎨 **${t("developments.detail.design_title")}**`);
                if (sourceDoc) {
                    lines.push(
                        `- **${t("ai_modal.design_base_doc_prefix")}** ${sourceDoc.name}`
                    );
                }
                const promptSummary = lastPrompt.replace(/<[^>]*>/g, "").trim();
                if (promptSummary) {
                    lines.push(
                        `- **${t("ai_modal.design_prompt_label")}:** ${promptSummary}`
                    );
                }
                //lines.push(`\n${t("ai_modal.design_files_attached_note")}`);
                messageContent = lines.join("\n");
            }

            const res = await developmentApi.sendMessage(development.id, {
                content: messageContent,
                is_internal: isDesignMode ? false : true,
                type: isDesignMode ? "ai_design_proposal" : "ai_response",
                metadata: {
                    prompt: lastPrompt,
                    conversation,
                    context_type: isDesignMode ? "design" : contextType,
                    requirement_attachment_id: isDesignMode
                        ? generatedRequirementDocId || selectedRequirementDocId
                        : undefined,
                    generated_by_ai: true,
                    ai_full_content:
                        isDesignMode && extractedHtml
                            ? undefined
                            : combinedContent,
                    ...aiMetadata,
                },
                reply_to_message_id: replyToMessageId,
                attachment_id: (aiMetadata as any).attachment_id,
            });

            if (res.result && res.data) {
                if (isDesignMode) {
                    showSuccess(t("ai_modal.design_saved_success"));
                }

                await onMessageAdded?.(res.data);
                onClose();
            } else {
                setError(res.message);
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : "Error");
        } finally {
            setLoading(false);
        }
    };

    const handleRedo = (): void => {
        if (aiMetadata) {
            void deleteOrphanedAttachment(aiMetadata);
        }
        if (lastPrompt) {
            setPrompt(lastPrompt);
        }
        setAiResult(null);
        setAiMetadata(null);
        setError(null);
    };

    const handleContinueAsking = (): void => {
        if (!aiResult) {
            return;
        }

        setConversation((prev) => [
            ...prev,
            { prompt: lastPrompt || prompt.trim(), content: aiResult },
        ]);
        setPrompt("");
        setAiResult(null);
        setAiMetadata(null);
        setAiAttachments([]);
        setUploadingFiles(false);
        setIsDraggingFile(false);
        dragCounter.current = 0;
        setFileViewerTarget(null);
        setShowLightbox(false);
        setError(null);
    };

    const handleCopy = (): void => {
        if (!aiResult) {
            return;
        }

        navigator.clipboard.writeText(aiResult);
        setCopied(true);
        setTimeout(() => setCopied(false), 2500);
    };

    const handleCopyChatText = (): void => {
        if (!conversationText) {
            return;
        }

        navigator.clipboard.writeText(conversationText);
        setChatCopied(true);
        setTimeout(() => setChatCopied(false), 2500);
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            onFullscreenChange={setIsFullscreen}
            title={
                isDesignMode ? t("ai_modal.design_title") : t("ai_modal.title")
            }
            maxWidth={isDesignMode || Boolean(standaloneHtml) ? "4xl" : "2xl"}
            footer={
                <div className="flex w-full justify-center gap-3">
                    <button
                        type="button"
                        onClick={handleClose}
                        disabled={loading}
                        className="btn secondary flex-1 min-w-[120px] max-w-[180px] justify-center cursor-pointer"
                    >
                        {t("ai_modal.close")}
                    </button>
                    <button
                        type="button"
                        onClick={aiResult ? handleAccept : handleGenerate}
                        disabled={
                            loading ||
                            (aiResult
                                ? !aiMetadata
                                : !prompt.replace(/<[^>]*>/g, "").trim())
                        }
                        className="btn primary flex-1 min-w-[120px] max-w-[180px] justify-center cursor-pointer shadow-md disabled:opacity-50"
                    >
                        {loading ? (
                            <>
                                <Icon
                                    value="loader"
                                    size={14}
                                    className="animate-spin"
                                />
                                <span>
                                    {aiResult
                                        ? isDesignMode
                                            ? t("ai_modal.design_export_saving")
                                            : t("ai_modal.accepting")
                                        : t("ai_modal.generating")}
                                </span>
                            </>
                        ) : (
                            <>
                                <span>
                                    {aiResult
                                        ? isDesignMode
                                            ? t("ai_modal.save_button")
                                            : t("ai_modal.ac_button")
                                        : isDesignMode
                                          ? t(
                                                "developments.detail.generate_design_button"
                                            )
                                          : t("ai_modal.generate_button")}
                                </span>
                            </>
                        )}
                    </button>
                </div>
            }
        >
            <div className="space-y-4">
                {!aiResult && (
                    <div className="justify-center items-center p-3 bg-primary/10 dark:bg-primary/20 border border-primary/25 rounded-xl text-xs text-primary dark:text-primary-300 flex gap-2">
                        <Icon
                            value={isDesignMode ? "palette" : "sparkles"}
                            size={15}
                            className="text-primary dark:text-primary-400 shrink-0"
                        />
                        <span>
                            {isDesignMode
                                ? t("ai_modal.design_subtitle")
                                : t("ai_modal.info_private")}
                        </span>
                    </div>
                )}

                {/* Base Requirements Document Selector in Design Mode */}
                {isDesignMode && !aiResult && (
                    <div className="flex flex-col gap-1.5 p-3 bg-slate-50 dark:bg-dark-subtle/40 rounded-xl border border-slate-200 dark:border-white/10">
                        <div className="flex items-center justify-between gap-2">
                            <span className="text-xs font-bold flex items-center gap-1.5 text-slate-700 dark:text-zinc-300">
                                <Icon
                                    value="fileText"
                                    size={14}
                                    className="text-primary"
                                />
                                {t("ai_modal.design_base_doc_label")}
                            </span>
                            <div className="flex items-center gap-1.5">
                                {requirementDocs.length > 0 && (
                                    <button
                                        type="button"
                                        onClick={() =>
                                            setIsDocSelectFullscreen(true)
                                        }
                                        className="p-1 rounded-full text-slate-400 dark:text-zinc-400 hover:text-primary transition-all cursor-pointer"
                                        title={t("common.fullscreen")}
                                    >
                                        <Icon value="maximize" size={16} />
                                    </button>
                                )}
                            </div>
                        </div>

                        {requirementDocs.length > 0 ? (
                            <SearchableMultiSelect
                                singleSelect
                                hideFullscreen
                                className="!mb-0"
                                isFullscreen={isDocSelectFullscreen}
                                onFullscreenChange={setIsDocSelectFullscreen}
                                options={requirementDocs.map((doc, index) => ({
                                    id: doc.id,
                                    name: doc.name,
                                    badge:
                                        index === 0 ? (
                                            <span className="text-[10px] font-semibold bg-emerald-100 dark:bg-emerald-950/50 text-emerald-700 dark:text-emerald-300 px-2 py-0.5 rounded-full border border-emerald-200 dark:border-emerald-800/40 shrink-0">
                                                {t(
                                                    "ai_modal.design_base_doc_latest"
                                                )}
                                            </span>
                                        ) : undefined,
                                }))}
                                value={
                                    selectedRequirementDocId
                                        ? [selectedRequirementDocId]
                                        : []
                                }
                                onChange={(ids) => {
                                    if (!loading) {
                                        setSelectedRequirementDocId(
                                            ids[0] ?? null
                                        );
                                        setAiResult(null);
                                        setAiMetadata(null);
                                    }
                                }}
                                placeholder={t(
                                    "ai_modal.design_base_doc_label"
                                )}
                            />
                        ) : (
                            <div className="text-[11px] text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/30 p-2 rounded-lg border border-amber-200 dark:border-amber-800/30">
                                {t("ai_modal.design_no_base_docs")}
                            </div>
                        )}
                        {/*
                        <div className="text-[11px] text-slate-500 dark:text-zinc-400 flex items-center gap-1">
                            <Icon value="info" size={12} className="shrink-0" />
                            <span>{t('ai_modal.design_phase2_context_info')}</span>
                        </div>
                        */}
                    </div>
                )}

                {/* Presets (Quick actions) */}
                {showQuickActions && !aiResult && (
                    <div className="mb-6">
                        <label className="text-center block text-xs font-bold tracking-wider text-slate-700 dark:text-zinc-300 mb-2">
                            {t("ai_modal.presets_label")}
                        </label>
                        <div className="grid grid-cols-2 gap-2">
                            {activePresets.map((p) => {
                                const isSelected = contextType === p.id;

                                return (
                                    <button
                                        key={p.id}
                                        type="button"
                                        onClick={() => handleApplyPreset(p)}
                                        className={`p-2.5 text-xs font-semibold rounded-xl border text-left transition-all cursor-pointer flex items-center space-x-2 ${
                                            isSelected
                                                ? "bg-primary/15 dark:bg-primary/25 border-primary text-primary dark:text-primary-300 shadow-xs ring-1 ring-primary/40"
                                                : "bg-slate-50 dark:bg-dark-subtle border-slate-200 dark:border-white/10 text-slate-700 dark:text-zinc-300 hover:bg-slate-100 dark:hover:bg-white/5"
                                        }`}
                                    >
                                        <Icon
                                            value={p.icon}
                                            size={15}
                                            className={`shrink-0 ${isSelected ? "text-primary dark:text-primary-400" : "text-slate-400 dark:text-zinc-500"}`}
                                        />
                                        <span className="truncate">
                                            {p.title}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* Attachments preview (if replying) */}
                {attachments && attachments.length > 0 && !aiResult && (
                    <div className="flex flex-col gap-1.5 p-3 bg-slate-50 dark:bg-dark-subtle/30 rounded-xl border border-slate-200 dark:border-white/10">
                        <span className="text-xs font-bold text-slate-700 dark:text-zinc-300 flex items-center gap-1.5">
                            <Icon value="paperclip" size={14} />
                            {t("developments.detail.attachments_title")}
                        </span>
                        <div className="flex flex-col gap-1 mt-1">
                            {attachments.map((att) => (
                                <AttachmentThumbnail
                                    key={att.id}
                                    name={att.name}
                                    mimeType={att.mime_type}
                                    fileSize={att.file_size}
                                    load={() =>
                                        developmentApi.getAttachmentBlob(
                                            development.id,
                                            att.id
                                        )
                                    }
                                    onClick={() => {}}
                                />
                            ))}
                        </div>
                    </div>
                )}

                {/* Response tone selector */}
                {!isDesignMode && !aiResult && (
                    <div>
                        <label className="text-center block text-xs font-bold text-slate-700 dark:text-zinc-300 mb-1.5">
                            {t("ai_modal.response_tone")}
                        </label>
                        <div className="flex justify-center items-center gap-2">
                            <button
                                type="button"
                                aria-pressed={contextLanguage === "it"}
                                onClick={() => handleSelectLanguage("it")}
                                disabled={loading}
                                className={`w-32 py-2 text-xs font-semibold rounded-xl border text-center transition-all cursor-pointer ${
                                    contextLanguage === "it"
                                        ? "btn-primary"
                                        : "btn-secondary"
                                }`}
                            >
                                <Icon value="computer" />
                                {t("ai_modal.tone_it")}
                            </button>
                            <button
                                type="button"
                                aria-pressed={contextLanguage === "client"}
                                onClick={() => handleSelectLanguage("client")}
                                disabled={loading}
                                className={`w-32 py-2 text-xs font-semibold rounded-xl border text-center transition-all cursor-pointer ${
                                    contextLanguage === "client"
                                        ? "btn-primary"
                                        : "btn-secondary"
                                }`}
                            >
                                <Icon value="user" />
                                {t("ai_modal.tone_client")}
                            </button>
                        </div>
                    </div>
                )}

                {/* AI Provider selector */}
                <div>
                    <label className="text-center block text-xs font-bold text-slate-700 dark:text-zinc-300 mb-1.5">
                        {t("ai_modal.provider_switch_title")}
                    </label>
                    <div className="flex justify-center items-center gap-2">
                        <button
                            type="button"
                            aria-pressed={aiProvider === "local"}
                            onClick={() => handleSelectProvider("local")}
                            disabled={loading}
                            className={`w-32 py-2 text-xs font-semibold rounded-xl border text-center transition-all cursor-pointer ${
                                aiProvider === "local"
                                    ? "btn-primary"
                                    : "btn-secondary"
                            }`}
                        >
                            <Icon value="server" />
                            {t("ai_modal.provider_local")}
                        </button>
                        <button
                            type="button"
                            aria-pressed={aiProvider === "openai"}
                            onClick={() => handleSelectProvider("openai")}
                            disabled={loading}
                            className={`w-32 py-2 text-xs font-semibold rounded-xl border text-center transition-all cursor-pointer ${
                                aiProvider === "openai"
                                    ? "btn-primary"
                                    : "btn-secondary"
                            }`}
                        >
                            <Icon value="sparkles" />
                            {t("ai_modal.provider_openai")}
                        </button>
                    </div>
                </div>

                {/* Include chat context switch */}
                {!aiResult && (
                    <div
                        className="cursor-pointer flex items-center justify-between gap-3 p-3 bg-primary/10 dark:bg-primary/20 border border-primary/25 rounded-xl hover:bg-primary/15 transition-all"
                        onClick={() => setIncludeContext(!includeContext)}
                    >
                        <div className="flex flex-col gap-0.5 min-w-0">
                            <span className="text-xs font-bold text-primary dark:text-primary flex items-center gap-1.5">
                                <Icon
                                    value={
                                        isDesignMode ? "palette" : "sparkles"
                                    }
                                    size={13}
                                    className="shrink-0"
                                />
                                {t("ai_modal.include_context_label")}
                            </span>
                            <span className="ml-5 text-[11px] text-primary/80 dark:text-primary/80">
                                {includeContext
                                    ? isDesignMode
                                        ? t(
                                              "ai_modal.design_phase2_context_info"
                                          )
                                        : t("ai_modal.include_context_on")
                                    : t("ai_modal.include_context_off")}
                            </span>
                        </div>
                        <Switch
                            checked={includeContext}
                            onChange={(val: boolean) => setIncludeContext(val)}
                            color="primary"
                            className="shrink-0"
                        />
                    </div>
                )}

                {/* Previous conversation turns */}
                {conversation.length > 0 && (
                    <div className="space-y-3">
                        {conversation.map((turn, idx) => (
                            <div
                                key={idx}
                                className="p-3 bg-slate-50 dark:bg-dark-subtle/30 rounded-xl border border-slate-200 dark:border-white/10 space-y-2 text-xs"
                            >
                                <div className="flex items-center gap-1.5 font-bold text-slate-700 dark:text-zinc-300">
                                    <Icon
                                        value="message"
                                        size={13}
                                        className="text-primary shrink-0"
                                    />
                                    <span>{turn.prompt}</span>
                                </div>
                                <div className="pl-3 border-l-2 border-primary/40 text-slate-800 dark:text-zinc-200 font-sans leading-relaxed">
                                    <Markdown>{turn.content}</Markdown>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Current query recap when response is present */}
                {aiResult && lastPrompt && (
                    <div className="p-3 bg-slate-50 dark:bg-dark-subtle/30 rounded-xl border border-slate-200 dark:border-white/10 text-xs">
                        <div className="flex items-start gap-2">
                            <Icon
                                value="message"
                                size={14}
                                className="text-primary shrink-0 mt-0.5"
                            />
                            <div className="flex flex-col gap-0.5 min-w-0">
                                <span className="text-[11px] font-bold text-slate-500 dark:text-zinc-400">
                                    {t("ai_modal.prompt_label")}
                                </span>
                                <span className="text-slate-800 dark:text-zinc-200 font-medium whitespace-pre-wrap">
                                    {lastPrompt}
                                </span>
                            </div>
                        </div>
                    </div>
                )}

                {/* Prompt input */}
                {!aiResult && (
                    <div className="flex flex-col gap-1.5">
                        <input
                            ref={inlineFileInputRef}
                            type="file"
                            multiple
                            onChange={handleAiFileUpload}
                            className="hidden"
                        />
                        <Textarea
                            inputWrapperProps={{
                                onDragEnter: handleDragEnter,
                                onDragOver: handleDragOver,
                                onDragLeave: handleDragLeave,
                                onDrop: handleDrop,
                            }}
                            overlayContent={
                                isDraggingFile && (
                                    <div className="absolute inset-0 z-50 flex items-center justify-center pointer-events-none border-2 border-dashed border-primary/50 bg-primary/15 dark:bg-primary/20 rounded-md">
                                        <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white/90 dark:bg-dark-box/90 text-primary dark:text-primary-400 font-semibold text-sm shadow-sm">
                                            <Icon
                                                value="uploadCloud"
                                                size={20}
                                                className="animate-bounce"
                                            />
                                            <span>
                                                {t(
                                                    "developments.detail.drop_files"
                                                )}
                                            </span>
                                        </div>
                                    </div>
                                )
                            }
                            label={
                                conversation.length > 0
                                    ? t("ai_modal.continue_asking_button")
                                    : isDesignMode
                                      ? t("ai_modal.design_prompt_label")
                                      : t("ai_modal.prompt_label")
                            }
                            labelRight={
                                <div className="flex items-center gap-1">
                                    <MicrophoneButton
                                        iconSize={16}
                                        className="p-1 rounded-full text-slate-500 dark:text-zinc-400 hover:text-primary transition-all cursor-pointer flex items-center justify-center"
                                        onTranscriptChange={(text) => {
                                            setBasePromptForRecording(
                                                (prev) => {
                                                    const base =
                                                        prev !== null
                                                            ? prev
                                                            : prompt;
                                                    setPrompt(
                                                        base +
                                                            (text
                                                                ? " " + text
                                                                : "")
                                                    );
                                                    return base;
                                                }
                                            );
                                        }}
                                        onCancel={() => {
                                            setBasePromptForRecording(
                                                (prev) => {
                                                    if (prev !== null) {
                                                        setPrompt(prev);
                                                    }
                                                    return null;
                                                }
                                            );
                                        }}
                                        onResult={(text) => {
                                            setBasePromptForRecording(
                                                (prev) => {
                                                    const base =
                                                        prev !== null
                                                            ? prev
                                                            : prompt;
                                                    const cleanedPrev =
                                                        base.trim() === ""
                                                            ? ""
                                                            : base;
                                                    setPrompt(
                                                        cleanedPrev
                                                            ? `${cleanedPrev} ${text}`
                                                            : text
                                                    );
                                                    return null;
                                                }
                                            );
                                        }}
                                    />
                                    <Tooltip
                                        title={t(
                                            "developments.detail.attach_files"
                                        )}
                                    >
                                        <button
                                            type="button"
                                            onClick={() =>
                                                inlineFileInputRef.current?.click()
                                            }
                                            disabled={uploadingFiles || loading}
                                            title={t(
                                                "developments.detail.attach_files"
                                            )}
                                            className="p-1 rounded-full text-slate-500 dark:text-zinc-400 hover:text-primary transition-all cursor-pointer disabled:opacity-50 flex items-center justify-center"
                                        >
                                            {uploadingFiles ? (
                                                <Icon
                                                    value="loader"
                                                    size={16}
                                                    className="animate-spin text-primary"
                                                />
                                            ) : (
                                                <Icon
                                                    value="paperclip"
                                                    size={16}
                                                />
                                            )}
                                        </button>
                                    </Tooltip>
                                </div>
                            }
                            aboveInput={renderAttachedChips()}
                            value={prompt}
                            onChange={(e) => {
                                setPrompt(e.target.value);
                            }}
                            onKeyDown={(e) => {
                                if (
                                    e.key === "Enter" &&
                                    (e.ctrlKey || e.metaKey)
                                ) {
                                    e.preventDefault();
                                    handleGenerate();
                                }
                            }}
                            placeholder={
                                conversation.length > 0
                                    ? t("ai_modal.prompt.followup_placeholder")
                                    : isDesignMode
                                      ? t("ai_modal.prompt.design_generic", {
                                            title: development?.title,
                                        })
                                      : isClientMode
                                        ? t("ai_modal.prompt.generic_client")
                                        : t("ai_modal.prompt.generic")
                            }
                            rows={conversation.length > 0 ? 3 : 5}
                            fullscreenTitle={
                                isDesignMode
                                    ? t("ai_modal.design_title")
                                    : t("ai_modal.title")
                            }
                            richTextFullscreen
                            fullscreenExtraContent={fullscreenExtraContent}
                            fullscreenFooter={(closeFullscreen) => (
                                <div className="flex items-center justify-center gap-3 w-full">
                                    <button
                                        type="button"
                                        onClick={closeFullscreen}
                                        className="btn secondary min-w-[120px] justify-center cursor-pointer"
                                    >
                                        {t("modal.close")}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            closeFullscreen();
                                            handleGenerate();
                                        }}
                                        disabled={
                                            loading ||
                                            (aiResult
                                                ? !aiMetadata
                                                : !prompt
                                                      .replace(/<[^>]*>/g, "")
                                                      .trim())
                                        }
                                        className="btn primary min-w-[120px] justify-center cursor-pointer shadow-md disabled:opacity-50"
                                    >
                                        {loading ? (
                                            <>
                                                <Icon
                                                    value="loader"
                                                    size={14}
                                                    className="animate-spin"
                                                />
                                                <span>
                                                    {aiResult
                                                        ? isDesignMode
                                                            ? t(
                                                                  "ai_modal.design_export_saving"
                                                              )
                                                            : t(
                                                                  "ai_modal.accepting"
                                                              )
                                                        : t(
                                                              "ai_modal.generating"
                                                          )}
                                                </span>
                                            </>
                                        ) : (
                                            <>
                                                <span>
                                                    {aiResult
                                                        ? isDesignMode
                                                            ? t(
                                                                  "ai_modal.save_button"
                                                              )
                                                            : t(
                                                                  "ai_modal.ac_button"
                                                              )
                                                        : isDesignMode
                                                          ? t(
                                                                "developments.detail.generate_design_button"
                                                            )
                                                          : t(
                                                                "ai_modal.generate_button"
                                                            )}
                                                </span>
                                            </>
                                        )}
                                    </button>
                                </div>
                            )}
                        />
                    </div>
                )}

                {error && (
                    <Alert variant="error" value={error} align="center" />
                )}

                {/* AI Result Card */}
                {aiResult && (
                    <>
                        {/* No tabs needed for preview since it's just the design */}
                        <div className="flex flex-col items-center justify-center gap-2 mb-3">
                            <div className="flex items-center justify-center gap-2 flex-wrap">
                                <button
                                    type="button"
                                    onClick={handleContinueAsking}
                                    className="w-30 justify-center flex items-center space-x-1.5 text-xs text-slate-700 dark:text-zinc-300 font-semibold bg-white dark:bg-[#1f1f23] px-3 py-1.5 rounded-lg border border-slate-300 dark:border-white/20 hover:bg-slate-50 dark:hover:bg-white/5 transition-colors shadow-xs cursor-pointer"
                                >
                                    <Icon value="reply" size={14} />
                                    <span>
                                        {t("ai_modal.continue_asking_button")}
                                    </span>
                                </button>
                                <button
                                    type="button"
                                    onClick={handleRedo}
                                    className="w-30 justify-center flex items-center space-x-1.5 text-xs text-slate-700 dark:text-zinc-300 font-semibold bg-white dark:bg-[#1f1f23] px-3 py-1.5 rounded-lg border border-slate-300 dark:border-white/20 hover:bg-slate-50 dark:hover:bg-white/5 transition-colors shadow-xs cursor-pointer"
                                >
                                    <Icon value="reload" size={14} />
                                    <span>{t("ai_modal.redo_button")}</span>
                                </button>
                                <button
                                    type="button"
                                    onClick={handleCopy}
                                    className="w-30 justify-center flex items-center space-x-1.5 text-xs text-slate-700 dark:text-zinc-300 font-semibold bg-white dark:bg-[#1f1f23] px-3 py-1.5 rounded-lg border border-slate-300 dark:border-white/20 hover:bg-slate-50 dark:hover:bg-white/5 transition-colors shadow-xs cursor-pointer"
                                >
                                    <Icon
                                        value={copied ? "check" : "copy"}
                                        size={14}
                                        className={
                                            copied ? "text-emerald-600" : ""
                                        }
                                    />
                                    <span>
                                        {copied
                                            ? t("ai_modal.copied")
                                            : t("ai_modal.copy_draft")}
                                    </span>
                                </button>
                                {(standaloneHtml ||
                                    (aiMetadata as any)?.attachment_url) && (
                                    <button
                                        type="button"
                                        onClick={handleOpenPreviewNewTab}
                                        title={t("ai_modal.open_new_tab")}
                                        className="w-30 justify-center flex items-center space-x-1.5 text-xs text-slate-700 dark:text-zinc-300 font-semibold bg-white dark:bg-[#1f1f23] px-3 py-1.5 rounded-lg border border-slate-300 dark:border-white/20 hover:bg-slate-50 dark:hover:bg-white/5 transition-colors shadow-xs cursor-pointer"
                                    >
                                        <Icon value="externalLink" size={14} />
                                        <span>
                                            {t("ai_modal.open_new_tab")}
                                        </span>
                                    </button>
                                )}
                            </div>
                            {(aiMetadata as any)?.usage &&
                                (() => {
                                    const usage = (aiMetadata as any).usage;
                                    const inTokens =
                                        usage.input_tokens ||
                                        usage.prompt_tokens ||
                                        usage.promptTokenCount ||
                                        usage.prompt_token_count ||
                                        0;
                                    const outTokens =
                                        usage.output_tokens ||
                                        usage.completion_tokens ||
                                        usage.candidatesTokenCount ||
                                        usage.candidates_token_count ||
                                        0;
                                    const totalTokens =
                                        usage.total_tokens ||
                                        usage.totalTokenCount ||
                                        usage.total_token_count ||
                                        inTokens + outTokens ||
                                        0;
                                    const cost = (aiMetadata as any).cost || 0;
                                    return totalTokens > 0 ? (
                                        <div className="flex items-center justify-center gap-3 text-[11px] leading-none font-mono text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-dark-box p-2.5 rounded-xl border border-slate-200 dark:border-white/10 shadow-xs w-full">
                                            {(aiMetadata as any)?.provider && (
                                                <span className="font-semibold text-slate-700 dark:text-slate-300 capitalize mr-1 flex items-center">
                                                    {
                                                        (aiMetadata as any)
                                                            .provider
                                                    }{" "}
                                                    {(aiMetadata as any)
                                                        .model && (
                                                        <span className="text-slate-400 font-normal ml-1">
                                                            {
                                                                (
                                                                    aiMetadata as any
                                                                ).model
                                                            }
                                                        </span>
                                                    )}
                                                </span>
                                            )}
                                            <Tooltip
                                                title={t(
                                                    "ai_modal.stats_in_tokens"
                                                )}
                                            >
                                                <span className="flex items-center gap-1 leading-none">
                                                    <Icon
                                                        value="arrowUp"
                                                        size={12}
                                                    />{" "}
                                                    {quantity(inTokens, 0)}
                                                </span>
                                            </Tooltip>
                                            <Tooltip
                                                title={t(
                                                    "ai_modal.stats_out_tokens"
                                                )}
                                            >
                                                <span className="flex items-center gap-1 leading-none">
                                                    <Icon
                                                        value="arrowDown"
                                                        size={12}
                                                    />{" "}
                                                    {quantity(outTokens, 0)}
                                                </span>
                                            </Tooltip>
                                            <Tooltip
                                                title={t(
                                                    "ai_modal.stats_total_tokens"
                                                )}
                                            >
                                                <span className="text-slate-600 dark:text-slate-300 flex items-center gap-1 leading-none">
                                                    <Icon
                                                        value="tally-5"
                                                        size={12}
                                                    />{" "}
                                                    {quantity(totalTokens, 0)}
                                                </span>
                                            </Tooltip>
                                            {cost !== undefined &&
                                                cost !== null && (
                                                    <Tooltip
                                                        title={t(
                                                            "ai_modal.stats_cost"
                                                        )}
                                                    >
                                                        <span className="font-bold text-emerald-600 dark:text-emerald-400 border-l border-slate-300 dark:border-white/10 pl-3 ml-1 flex items-center gap-1 leading-none">
                                                            <Icon
                                                                value="coins"
                                                                size={12}
                                                            />{" "}
                                                            {price(
                                                                cost,
                                                                "euro",
                                                                "0.00000€",
                                                                5
                                                            )}
                                                        </span>
                                                    </Tooltip>
                                                )}
                                        </div>
                                    ) : null;
                                })()}
                        </div>

                        {(standaloneHtml ||
                            (aiMetadata as any)?.attachment_url) &&
                        viewTab === "preview" ? (
                            <div className="p-10 bg-slate-50 dark:bg-dark-subtle/30 rounded-xl border border-slate-200 dark:border-white/10 flex flex-col items-center justify-center gap-4 text-center">
                                <div className="p-4 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 rounded-full shadow-inner">
                                    <Icon value="checkCircle" size={40} />
                                </div>
                                <div>
                                    <h3 className="text-base font-bold text-slate-800 dark:text-zinc-200 mb-1">
                                        {t("ai_modal.design_generated_title")}
                                    </h3>
                                    {/*<p className="text-sm text-slate-500 dark:text-zinc-400 max-w-md mx-auto">{t("ai_modal.design_generated_desc")}</p>*/}
                                </div>
                                <button
                                    type="button"
                                    onClick={handleOpenPreviewNewTab}
                                    className="btn primary mt-3 cursor-pointer shadow-md hover:shadow-lg transition-all"
                                >
                                    <Icon value="externalLink" size={16} />
                                    <span>{t("ai_modal.open_design")}</span>
                                </button>
                            </div>
                        ) : (
                            <div className="p-4 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 rounded-xl space-y-3 animate-fade-in">
                                <Markdown className="text-xs text-slate-800 dark:text-zinc-200 font-sans leading-relaxed">
                                    {standaloneHtml
                                        ? t(
                                              "ai_modal.design_documents_generated_message"
                                          )
                                        : aiResult}
                                </Markdown>
                            </div>
                        )}
                    </>
                )}
            </div>

            {fileViewerTarget && (
                <FileViewer
                    isOpen={Boolean(fileViewerTarget)}
                    onClose={() => setFileViewerTarget(null)}
                    title={fileViewerTarget.filename}
                    filename={fileViewerTarget.filename}
                    mimeType={fileViewerTarget.mimeType}
                    fileSize={fileViewerTarget.fileSize}
                    loadBlob={fileViewerTarget.loadBlob}
                    allowDownload={true}
                    zIndex={130000}
                />
            )}

            <ImageLightboxModal
                isOpen={showLightbox}
                onClose={() => setShowLightbox(false)}
                items={lightboxItems}
                initialIndex={lightboxIndex}
            />
        </Modal>
    );
};

export default AskAiModal;
